import style from './bnum-card-title.less';
import { HTMLBnumIcon } from '../../atoms/icon/bnum-icon';
import { TAG_CARD_TITLE } from '../../../core/utils/constants/tags';
import { Nullable } from '@rotomeca/utils';
import { Scheduler } from '../../../core/utils/Scheduler';
import { Attr, Define } from '../../../core/decorators';
import BnumElementInternal from 'components/bnum-element-states';
import { UpdateAll } from 'core/decorators/updateAll';
import { Observe } from 'core/decorators/observe';
import { UI } from 'core/decorators/ui';
import { Schedule } from 'core/decorators/schedule';
import { Listener } from 'core/decorators/listener';
import JsEvent from '@rotomeca/event';

//#region Global Constants
const ATTRIBUTE_URL = 'url';
const ATTRIBUTE_DATA_ICON = 'icon';
const SLOT_NAME_ICON = 'icon';
const CLASS_LINK = 'card-title-link';
const STATE_URL = 'url';
const STATE_WITHOUT_URL = 'without-url';
const CLASS_ICON_TITLE = 'card-icon-title';
const ID_SLOT_ICON = 'sloticon';
const ID_SLOT_TEXT = 'mainslot';
const ID_CUSTOM_BODY = 'custombody';
//#endregion Global Constants

//#region Template
const TEMPLATE = (
	<h2>
		<a class={CLASS_LINK}>
			<span class="container">
				<slot id={ID_SLOT_ICON} name={SLOT_NAME_ICON}></slot>
				<HTMLBnumIcon class={CLASS_ICON_TITLE} hidden></HTMLBnumIcon>
			</span>
			<span class="container">
				<slot id={ID_SLOT_TEXT}></slot>
				<span id={ID_CUSTOM_BODY} hidden></span>
			</span>
		</a>
	</h2>
);
//#endregion Template

/**
 * Composant représentant le titre d'une carte, pouvant inclure une icône et un lien.
 *
 * Permet d'afficher un titre enrichi avec une icône et éventuellement un lien cliquable.
 *
 * @category Card
 *
 * @structure Cas url et icône
 * <bnum-card-title data-icon="labs" url="https://example.com">Titre de la carte</bnum-card-title>
 *
 * @structure Cas icône uniquement
 * <bnum-card-title data-icon="labs">Titre de la carte</bnum-card-title>
 *
 * @structure Cas lien uniquement
 * <bnum-card-title url="https://example.com">Titre de la carte</bnum-card-title>
 *
 * @structure Cas texte seul
 * <bnum-card-title>Titre de la carte</bnum-card-title>
 *
 * @structure Cas icône via slot
 * <bnum-card-title>
 * <bnum-icon slot="icon">drive_folder_upload</bnum-icon>
 * Titre de la carte
 * </bnum-card-title>
 *
 * @state url - Actif lorsque le titre contient un lien.
 * @state without-url - Actif lorsque le titre ne contient pas de lien.
 *
 * @slot (default) - Titre de la carte (texte ou HTML)
 * @slot icon - Icône personnalisée à afficher avant le titre. Note: si une icône est définie via l'attribut `data-icon` ou via la propriété `icon`, ce slot sera ignoré.
 *
 * @attr {string | null} (optional) url - URL du lien du titre de la carte
 * @attr {string | null} (optional) data-icon - Nom de l'icône (Material Symbols) à afficher avant le titre
 *
 * @cssvar {flex} --bnum-card-title-display - Définit le mode d'affichage du titre de la carte.
 * @cssvar {center} --bnum-card-title-align-items - Définit l'alignement vertical des éléments dans le titre de la carte.
 * @cssvar {var(--bnum-space-s, 10px)} --bnum-card-title-gap - Définit l'espacement entre l'icône et le texte du titre.
 */
@Define({ tag: TAG_CARD_TITLE, styles: style, template: TEMPLATE })
@UpdateAll()
@Observe(ATTRIBUTE_URL)
export class HTMLBnumCardTitle extends BnumElementInternal {
	//#region Private fields
	#_bodyScheduler: Nullable<Scheduler<HTMLElement | string | Text>> = null;
	#_initBody: Nullable<HTMLElement | string | Text> = null;
	#_initUrlListener: boolean = false;
	//#endregion Private fields

	//#region Getter/Setters
	@UI({
		slotIcon: `#${ID_SLOT_ICON}`,
		slotText: `#${ID_SLOT_TEXT}`,
		customBody: `#${ID_CUSTOM_BODY}`,
		link: `.${CLASS_LINK}`,
		icon: `.${CLASS_ICON_TITLE}`,
	})
	accessor #_ui!: {
		slotIcon: HTMLSlotElement;
		slotText: HTMLSlotElement;
		customBody: HTMLSpanElement;
		link: HTMLAnchorElement;
		icon: HTMLBnumIcon;
	};

	@Listener() accessor onurlclick!: JsEvent<
		(e: CustomEvent<{ inner: MouseEvent }>) => void
	>;

	/**
	 * URL du lien du titre de la carte.
	 */
	@Attr() accessor url: Nullable<string> = null;

	/**
	 * Obtient le nom de l'icône associée au titre de la carte.
	 * @returns {string | null} Nom de l'icône ou null si aucune icône n'est définie
	 */
	public get icon(): string | null {
		return this.data(ATTRIBUTE_DATA_ICON);
	}

	/**
	 * Définit le nom de l'icône associée au titre de la carte.
	 * Met à jour le DOM pour refléter le changement.
	 * @param {string | null} v Nom de l'icône ou null
	 */
	public set icon(v: string | null) {
		if (this.alreadyLoaded) {
			this._p_setData(ATTRIBUTE_DATA_ICON, v)._updateDOM();
		} else {
			const fromAttribute = true;
			this.data(ATTRIBUTE_DATA_ICON, v, fromAttribute);
		}
	}
	//#endregion Getter/Setters

	//#region Lifecycle
	/**
	 * Constructeur du composant HTMLBnumCardTitle.
	 * Initialise le composant sans ajouter d'éléments DOM.
	 */
	constructor() {
		super();
	}

	/**
	 * Construit le DOM du composant dans le conteneur donné.
	 *
	 * Ajoute l'icône, le texte et le lien selon les propriétés définies.
	 */
	protected _p_buildDOM(): void {
		this._updateDOM();

		if (this.#_initBody) {
			this.#_updateBody(this.#_initBody);
			this.#_initBody = null;
		}
	}

	/**
	 * Méthode appelée lors de la mise à jour d'un attribut observé.
	 * Met à jour le DOM du composant.
	 */
	protected _p_update(): void {
		if (this.alreadyLoaded) this._updateDOM();
	}
	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Met à jour le DOM du composant selon les propriétés actuelles.
	 * Affiche ou masque l'icône et met à jour le lien si nécessaire.
	 * @private
	 */
	@Schedule()
	private _updateDOM(): void {
		const url = this.url;
		const icon = this.icon;

		this._p_clearStates();

		if (icon) {
			this.#_ui.icon.icon = icon;
			this.#_ui.icon.hidden = false;
			this.#_ui.slotIcon.hidden = true;
		} else this.#_ui.icon.hidden = true;

		if (url) {
			this.#_ui.link.href = url;
			this._p_addState(STATE_URL);

			this.#_ui.link.removeAttribute('role');
			this.#_ui.link.removeAttribute('aria-disabled');

			if (!this.#_initUrlListener) {
				this.#_ui.link.addEventListener('click', e => {
					this.trigger(
						'bnum-card-title:url.click',
						{ inner: e },
						{ bubbles: e.bubbles, cancelable: e.cancelable },
					);
				});

				this.addEventListener('bnum-card-title:url.click', e => {
					this.onurlclick.call(e);
				});

				this.#_initUrlListener = true;
			}
		} else {
			this.#_ui.link.removeAttribute('href');
			this._p_addState(STATE_WITHOUT_URL);
		}
	}

	/**
	 * Met à jour le corps du titre de la carte.
	 * @param element Elément HTML, texte ou nœud Text à insérer dans le titre
	 * @private
	 */
	#_updateBody(element: HTMLElement | string | Text): void {
		this.#_ui.customBody.hidden = false;
		this.#_ui.slotText.hidden = true;

		if (typeof element === 'string') this.#_ui.customBody.textContent = element;
		else this.#_ui.customBody.appendChild(element);
	}
	//#endregion Private methods

	//#region Public methods

	/**
	 * Met à jour le contenu du titre de la carte.
	 * Remplace le texte ou ajoute un élément HTML comme corps du titre.
	 * @param {HTMLElement | string | Text} element Le contenu à insérer (texte, élément ou nœud Text)
	 * @returns {HTMLBnumCardTitle} Retourne l'instance pour chaînage
	 */
	public updateBody(
		element: HTMLElement | string | Text,
		{ force = false } = {},
	): HTMLBnumCardTitle {
		this.#_bodyScheduler ??= new Scheduler<HTMLElement | string | Text>(
			(el: Nullable<HTMLElement | string | Text>) => {
				this.#_updateBody(el!);
			},
		);

		if (!this.alreadyLoaded) this.#_initBody = element;
		else if (force) this.#_bodyScheduler.call(element);
		else this.#_bodyScheduler.schedule(element);

		return this;
	}
	//#endregion Public methods

	//#region Static methods
	/**
	 * Crée dynamiquement une instance du composant HTMLBnumCardTitle.
	 * Permet d'initialiser le titre avec un texte, une icône et/ou un lien.
	 * @param {HTMLElement | string | Text} text Le contenu du titre (élément, texte ou chaîne)
	 * @param {{ icon?: string | null; link?: string | null }} options Options pour l'icône et le lien
	 * @returns {HTMLBnumCardTitle} Instance du composant configurée
	 */
	public static Create(
		text: HTMLElement | string | Text,
		{
			icon = null,
			link = null,
		}: { icon?: string | null; link?: string | null },
	): HTMLBnumCardTitle {
		const node: HTMLBnumCardTitle = document.createElement(
			this.TAG,
		) as HTMLBnumCardTitle;
		if (icon) node.icon = icon;
		if (link) node.url = link;

		return node.updateBody(text, { force: true });
	}

	/**
	 * Génère le HTML d'un titre de carte avec icône et lien optionnels.
	 * Utile pour créer dynamiquement le composant dans une chaîne HTML.
	 * @param {string | null} icon Icône à afficher
	 * @param {string} text Texte du titre
	 * @param {string | null} link URL du lien
	 * @returns {string} HTML généré
	 */
	public static Generate(
		icon: string | null,
		text: string,
		link: string | null,
	): string {
		const data: Record<string, string> = {};

		if (icon) data['data-icon'] = icon;
		if (link) data.url = link;

		if (data.url || data['data-icon'])
			return <HTMLBnumCardTitle {...data}>{text}</HTMLBnumCardTitle>;
		else return <HTMLBnumCardTitle>{text}</HTMLBnumCardTitle>;
	}

	//#endregion Static methods
}
